import { useEffect, useState, useCallback } from 'react';
import {
  Upload, FileText, CheckCircle2, AlertTriangle, Clock, Loader2,
  ExternalLink, Trash2, Image as ImageIcon, Download, X
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

/**
 * InfrastructureDocs — Task #150 Nivå C
 *
 * Systemadmin-side for dokumentasjon av eksterne tjenester vi ikke har API-tilgang til
 * (ProISP-kundeportal, Tornado hypervisor-admin, DNS/SSL, Buypass eSeal).
 *
 * Erstatter at kritisk DR-dokumentasjon ligger begravd i OneDrive — alt samles her
 * med skjermdump + fritekst + tydelig "sist verifisert"-dato og halvårlig varsel.
 */

interface InfrastructureDoc {
  id: string;
  service_slug: string;
  service_name: string;
  description: string | null;
  screenshot_path: string | null;
  screenshot_mime_type: string | null;
  screenshot_size_bytes: number | null;
  screenshot_original_name: string | null;
  screenshot_uploaded_at: string | null;
  free_text: string | null;
  last_updated_at: string | null;
  last_updated_by: string | null;
  last_updated_by_name: string | null;
  warning_threshold_days: number;
  display_order: number;
  days_since_update: number | null;
  status: 'current' | 'overdue' | 'never_verified';
  has_screenshot: boolean;
}

export default function InfrastructureDocs() {
  const [services, setServices] = useState<InfrastructureDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingSlug, setSavingSlug] = useState<string | null>(null);
  const [uploadingSlug, setUploadingSlug] = useState<string | null>(null);
  const [editingTexts, setEditingTexts] = useState<Record<string, { free_text: string; description: string }>>({});
  const [previewSlug, setPreviewSlug] = useState<string | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newService, setNewService] = useState({ slug: '', name: '', description: '', threshold: 180 });

  async function fetchBlob(slug: string): Promise<string | null> {
    const res = await authenticatedFetch(`/api/admin/infrastructure-docs/${slug}/screenshot`);
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }

  async function openPreview(slug: string) {
    setPreviewSlug(slug);
    setPreviewLoading(true);
    setPreviewBlobUrl(null);
    const url = await fetchBlob(slug);
    setPreviewBlobUrl(url);
    setPreviewLoading(false);
  }

  function closePreview() {
    setPreviewSlug(null);
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
  }

  async function downloadFile(slug: string, filename: string) {
    const url = await fetchBlob(slug);
    if (!url) {
      alert('Kunne ikke laste ned.');
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch('/api/admin/infrastructure-docs/');
      if (!res.ok) {
        setError(`Kunne ikke hente tjenester (HTTP ${res.status}).`);
        return;
      }
      const data = await res.json();
      const list: InfrastructureDoc[] = data.services || [];
      setServices(list);
      const edits: Record<string, { free_text: string; description: string }> = {};
      for (const s of list) {
        edits[s.service_slug] = {
          free_text: s.free_text || '',
          description: s.description || '',
        };
      }
      setEditingTexts(edits);
    } catch (e) {
      setError('Nettverksfeil ved henting.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  async function saveText(slug: string) {
    const payload = editingTexts[slug];
    if (!payload) return;
    setSavingSlug(slug);
    try {
      const res = await authenticatedFetch(`/api/admin/infrastructure-docs/${slug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ free_text: payload.free_text, description: payload.description }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(`Kunne ikke lagre: ${data.error || res.status}`);
        return;
      }
      await fetchServices();
    } finally {
      setSavingSlug(null);
    }
  }

  async function markUpdated(slug: string) {
    if (!confirm('Marker denne tjenesten som verifisert i dag? Dette nullstiller varsel-timeren.')) return;
    setSavingSlug(slug);
    try {
      const res = await authenticatedFetch(`/api/admin/infrastructure-docs/${slug}/mark-updated`, {
        method: 'POST',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(`Kunne ikke oppdatere: ${data.error || res.status}`);
        return;
      }
      await fetchServices();
    } finally {
      setSavingSlug(null);
    }
  }

  async function uploadScreenshot(slug: string, file: File) {
    setUploadingSlug(slug);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authenticatedFetch(`/api/admin/infrastructure-docs/${slug}/upload`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(`Opplasting feilet: ${data.error || res.status}${data.max_mb ? ` (maks ${data.max_mb} MB)` : ''}`);
        return;
      }
      await fetchServices();
    } finally {
      setUploadingSlug(null);
    }
  }

  async function deleteScreenshot(slug: string) {
    if (!confirm('Slett opplastet skjermdump? Beholder fritekst og dato.')) return;
    const res = await authenticatedFetch(`/api/admin/infrastructure-docs/${slug}/screenshot`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      alert(`Kunne ikke slette (HTTP ${res.status}).`);
      return;
    }
    await fetchServices();
  }

  async function addService() {
    if (!newService.slug || !newService.name) {
      alert('Slug og navn er påkrevd.');
      return;
    }
    const res = await authenticatedFetch('/api/admin/infrastructure-docs/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_slug: newService.slug,
        service_name: newService.name,
        description: newService.description || null,
        warning_threshold_days: newService.threshold,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(`Kunne ikke opprette: ${data.error || res.status}`);
      return;
    }
    setNewService({ slug: '', name: '', description: '', threshold: 180 });
    setShowAddForm(false);
    await fetchServices();
  }

  function statusBadge(s: InfrastructureDoc) {
    if (s.status === 'current') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle2 size={12} /> Oppdatert
        </span>
      );
    }
    if (s.status === 'overdue') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-800">
          <AlertTriangle size={12} /> Trenger oppdatering ({s.days_since_update}d)
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
        <Clock size={12} /> Aldri verifisert
      </span>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-600 p-6">
        <Loader2 size={18} className="animate-spin" /> Henter tjenester…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border p-4 md:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Infrastrukturdokumentasjon</h2>
            <p className="text-sm text-gray-600 mt-1 max-w-3xl">
              Dokumentasjon av eksterne tjenester der vi ikke har API-tilgang til konfigurasjonen
              (ProISP, Tornado hypervisor, DNS/SSL, Buypass eSeal). Last opp skjermdump av innstillinger
              og noter kritiske detaljer så disaster recovery er reproduserbart.
              Varsel sendes etter angitt antall dager uten verifisering.
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="text-sm px-3 py-2 rounded border border-gray-300 hover:bg-gray-50"
          >
            {showAddForm ? 'Avbryt' : '+ Ny tjeneste'}
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            {error}
          </div>
        )}

        {showAddForm && (
          <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Slug (kebab-case)</label>
                <input
                  value={newService.slug}
                  onChange={e => setNewService(s => ({ ...s, slug: e.target.value }))}
                  placeholder="f.eks. twilio-sms-portal"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Navn</label>
                <input
                  value={newService.name}
                  onChange={e => setNewService(s => ({ ...s, name: e.target.value }))}
                  placeholder="f.eks. Twilio SMS-portal"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Beskrivelse (hva dokumenteres her?)</label>
              <textarea
                value={newService.description}
                onChange={e => setNewService(s => ({ ...s, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-gray-700">Varselterskel (dager):</label>
              <input
                type="number"
                min={7}
                max={1825}
                value={newService.threshold}
                onChange={e => setNewService(s => ({ ...s, threshold: parseInt(e.target.value, 10) || 180 }))}
                className="w-24 px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <button
                onClick={addService}
                className="ml-auto text-sm px-3 py-2 bg-kf-blue-600 text-white rounded hover:bg-kf-blue-700"
              >
                Opprett
              </button>
            </div>
          </div>
        )}
      </div>

      {services.map(s => (
        <div key={s.id} className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 md:p-5 border-b border-gray-100">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-semibold text-gray-900">{s.service_name}</h3>
                  {statusBadge(s)}
                </div>
                {s.description && (
                  <p className="text-sm text-gray-600 mt-1">{s.description}</p>
                )}
                <div className="text-xs text-gray-500 mt-2 flex items-center gap-3 flex-wrap">
                  {s.last_updated_at ? (
                    <>
                      <span>
                        Sist verifisert: {new Date(s.last_updated_at).toLocaleDateString('no-NO', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                        })}
                        {s.last_updated_by_name && ` av ${s.last_updated_by_name}`}
                      </span>
                      <span className="text-gray-400">·</span>
                      <span>Varselterskel: {s.warning_threshold_days} dager</span>
                    </>
                  ) : (
                    <span>Varselterskel: {s.warning_threshold_days} dager</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => markUpdated(s.service_slug)}
                  disabled={savingSlug === s.service_slug}
                  className="text-sm px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                >
                  {savingSlug === s.service_slug ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Verifisert nå
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 md:p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Venstre: fritekst */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText size={14} className="inline mr-1" />
                Konfigurasjonsnotater
              </label>
              <textarea
                value={editingTexts[s.service_slug]?.free_text ?? ''}
                onChange={e => setEditingTexts(prev => ({
                  ...prev,
                  [s.service_slug]: { ...prev[s.service_slug], free_text: e.target.value },
                }))}
                rows={10}
                placeholder="Eksempel for ProISP: DNS-oppføringer (A, MX, TXT), SMTP-innstillinger, domenefornying-dato, kundenummer, support-telefon, URL til kundeportal, 2FA-detaljer. Ingen passord — kun kontekst for DR."
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => saveText(s.service_slug)}
                  disabled={savingSlug === s.service_slug}
                  className="text-sm px-3 py-2 bg-kf-blue-600 text-white rounded hover:bg-kf-blue-700 disabled:opacity-50"
                >
                  {savingSlug === s.service_slug ? 'Lagrer…' : 'Lagre notater'}
                </button>
                <span className="text-xs text-gray-500">Lagring teller også som verifisering.</span>
              </div>
            </div>

            {/* Høyre: skjermdump */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ImageIcon size={14} className="inline mr-1" />
                Skjermdump (PNG/JPG/WebP/PDF, maks 20 MB)
              </label>
              {s.has_screenshot ? (
                <div className="border border-gray-200 rounded p-3 bg-gray-50">
                  <div className="text-xs text-gray-700">
                    <div className="font-medium text-gray-900">{s.screenshot_original_name}</div>
                    <div className="text-gray-500 mt-1">
                      {s.screenshot_mime_type} ·
                      {' '}{s.screenshot_size_bytes ? Math.round(s.screenshot_size_bytes / 1024) : 0} KB
                      {s.screenshot_uploaded_at && (
                        <> · Lastet opp {new Date(s.screenshot_uploaded_at).toLocaleDateString('no-NO')}</>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => openPreview(s.service_slug)}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-white flex items-center gap-1"
                    >
                      <ExternalLink size={14} /> Vis
                    </button>
                    <button
                      onClick={() => downloadFile(s.service_slug, s.screenshot_original_name || 'screenshot')}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-white flex items-center gap-1"
                    >
                      <Download size={14} /> Last ned
                    </button>
                    <label className="text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-white flex items-center gap-1 cursor-pointer">
                      <Upload size={14} /> Erstatt
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
                        className="hidden"
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f) uploadScreenshot(s.service_slug, f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    <button
                      onClick={() => deleteScreenshot(s.service_slug)}
                      className="ml-auto text-sm px-3 py-1.5 border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1"
                    >
                      <Trash2 size={14} /> Fjern
                    </button>
                  </div>
                </div>
              ) : (
                <label className="block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50">
                  {uploadingSlug === s.service_slug ? (
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                      <Loader2 size={18} className="animate-spin" />
                      Laster opp…
                    </div>
                  ) : (
                    <>
                      <Upload size={24} className="mx-auto text-gray-400 mb-2" />
                      <div className="text-sm text-gray-700 font-medium">Klikk for å laste opp</div>
                      <div className="text-xs text-gray-500 mt-1">PNG, JPG, WebP eller PDF (maks 20 MB)</div>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) uploadScreenshot(s.service_slug, f);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>
          </div>
        </div>
      ))}

      {services.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-sm border p-6 text-center text-gray-500">
          Ingen tjenester registrert ennå. Klikk "+ Ny tjeneste" for å legge til.
        </div>
      )}

      {/* Preview modal */}
      {previewSlug && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-lg max-w-5xl max-h-[90vh] w-full overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <div className="text-sm font-medium text-gray-900">
                {services.find(s => s.service_slug === previewSlug)?.service_name}
              </div>
              <button
                onClick={closePreview}
                className="p-1 rounded hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-4">
              {previewLoading ? (
                <div className="flex items-center gap-2 text-gray-600">
                  <Loader2 size={20} className="animate-spin" /> Laster inn…
                </div>
              ) : previewBlobUrl ? (
                (() => {
                  const svc = services.find(s => s.service_slug === previewSlug);
                  if (svc?.screenshot_mime_type === 'application/pdf') {
                    return <iframe src={previewBlobUrl} className="w-full h-[80vh] border-0" title="PDF-preview" />;
                  }
                  return <img src={previewBlobUrl} alt="Skjermdump" className="max-w-full max-h-full object-contain" />;
                })()
              ) : (
                <div className="text-sm text-red-700">Kunne ikke laste filen.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
